import { useEffect } from 'react';
import { useAppStore } from '../store/useAppStore';
import { translateUi, type UiLanguage } from '../i18n/uiTranslations';

const originals = new WeakMap<Node, string>();
const lastApplied = new WeakMap<Node, string>();
const attributeOriginals = new WeakMap<Element, Map<string, string>>();
const attributes = ['aria-label', 'title', 'placeholder'];

function translateTree(root: Node, language: UiLanguage) {
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT);
  const nodes: Text[] = root instanceof Text ? [root] : [];
  let current = walker.nextNode();
  while (current) {
    nodes.push(current as Text);
    current = walker.nextNode();
  }
  for (const node of nodes) {
    if (!originals.has(node) || node.data !== lastApplied.get(node)) originals.set(node, node.data);
    const initial = originals.get(node) ?? node.data;
    originals.set(node, initial);
    const trimmed = initial.trim();
    if (!trimmed) continue;
    const translated = initial.replace(trimmed, translateUi(trimmed, language));
    lastApplied.set(node, translated);
    if (node.data !== translated) node.data = translated;
  }
  const elements =
    root instanceof Element
      ? [root, ...root.querySelectorAll('*')]
      : root instanceof Document
        ? [...root.querySelectorAll('*')]
        : [];
  for (const element of elements)
    for (const attribute of attributes) {
      const currentValue = element.getAttribute(attribute);
      if (!currentValue) continue;
      let values = attributeOriginals.get(element);
      if (!values) {
        values = new Map();
        attributeOriginals.set(element, values);
      }
      const initial = values.get(attribute) ?? currentValue;
      values.set(attribute, initial);
      element.setAttribute(attribute, translateUi(initial, language));
    }
}

export function useUiLanguage() {
  const language = useAppStore((state) =>
    state.settings.language === 'id' ? 'id' : 'en',
  ) as UiLanguage;
  useEffect(() => {
    document.documentElement.lang = language;
    translateTree(document, language);
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'characterData') translateTree(mutation.target, language);
        for (const node of mutation.addedNodes) translateTree(node, language);
      }
    });
    observer.observe(document.body, { childList: true, characterData: true, subtree: true });
    return () => observer.disconnect();
  }, [language]);
}
